import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import { useCollection, indexById } from '../../runtime/mobile/useCollection';
import { TEST_WORKER_URL } from '../../runtime/mobile/testWorkerClient';
import { notify } from '../../ui/dialogs';
import { colors, radius, spacing, typography } from '../../config/theme';
import { Card, Field, Loader, PrimaryButton, SectionLabel, TextButton, Toggle, screen } from '../ui';
import { StickerBackdrop } from '../brand';

const DOW = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']; // index 0..6 → weekday 1..7 (7=вс → stored as 0)
const toWeekday = (i) => (i === 6 ? 0 : i + 1); // chip index → slot.weekday (0=вс,1=пн..6=сб)
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const short = (n) => (n || '?').trim().split(/\s+/).slice(0, 2).join(' ');

export default function ScheduleTemplateScreen({ route }) {
  const { user } = useAuth();
  const locations = useCollection('locations');
  const shiftTypes = useCollection('shiftTypes');
  const users = useCollection('users');

  const activeLocs = useMemo(() => locations.docs.filter((l) => l.active), [locations.docs]);
  const [locId, setLocId] = useState(route?.params?.locationId || null);
  useEffect(() => { if (!locId && activeLocs.length) setLocId(activeLocs[0].id); }, [activeLocs, locId]);

  const [active, setActive] = useState(true);
  const [slots, setSlots] = useState([]); // { weekday, shiftTypeId, employeeId, position, startLocal, endLocal }
  const [dayIdx, setDayIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(null);

  const loc = useMemo(() => indexById(locations.docs)[locId], [locations.docs, locId]);
  const stForLoc = useMemo(
    () => shiftTypes.docs.filter((s) => s.active),
    [shiftTypes.docs],
  );
  const usersForLoc = useMemo(
    () => users.docs.filter((u) => u.active && (u.locationIds || []).includes(locId)),
    [users.docs, locId],
  );
  const stName = (id) => indexById(shiftTypes.docs)[id]?.name || id;
  const empName = (id) => short(indexById(users.docs)[id]?.fullName || indexById(users.docs)[id]?.email || id);

  useEffect(() => {
    if (!locId || !user) return;
    setLoading(true);
    (async () => {
      try {
        const token = await user.getIdToken();
        const r = await fetch(`${TEST_WORKER_URL}/schedule/template/get`, {
          method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
          body: JSON.stringify({ locationId: locId }),
        });
        const j = await r.json();
        setSlots(j.template?.slots || []);
        setActive(j.template ? j.template.active !== false : true);
      } catch { setSlots([]); }
      setLoading(false);
    })();
  }, [locId, user]);

  const daySlots = slots
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.weekday === toWeekday(dayIdx))
    .sort((a, b) => a.s.startLocal.localeCompare(b.s.startLocal));

  const startAdd = () => {
    setDraft({
      shiftTypeId: stForLoc[0]?.id || '',
      employeeId: usersForLoc[0]?.id || '',
      position: null,
      startLocal: loc?.operatingHours?.open || '09:00',
      endLocal: loc?.operatingHours?.close || '21:00',
    });
    setAdding(true);
  };
  const commitAdd = () => {
    if (!draft.shiftTypeId || !draft.employeeId) { notify('Не хватает', 'Выберите сотрудника и тип смены'); return; }
    if (!HHMM.test(draft.startLocal) || !HHMM.test(draft.endLocal)) { notify('Время', 'Формат ЧЧ:ММ, например 09:00'); return; }
    setSlots((prev) => [...prev, { ...draft, weekday: toWeekday(dayIdx) }]);
    setAdding(false); setDraft(null);
  };
  const removeSlot = (idx) => setSlots((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const token = await user.getIdToken();
      const r = await fetch(`${TEST_WORKER_URL}/schedule/template`, {
        method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ locationId: locId, active, slots }),
      });
      const j = await r.json();
      if (!r.ok) notify('Не сохранилось', j.message || j.error || 'Ошибка');
      else notify('Готово', `Шаблон сохранён — ${j.slots} смен(ы) в неделю`);
    } catch { notify('Ошибка', 'Нет связи'); }
    setSaving(false);
  };
  const apply = async () => {
    setApplying(true);
    try {
      const token = await user.getIdToken();
      const r = await fetch(`${TEST_WORKER_URL}/schedule/template/apply`, {
        method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ locationId: locId, days: 14 }),
      });
      const j = await r.json();
      if (!r.ok) notify('Не вышло', j.message || j.error || 'Ошибка');
      else notify('График заполнен', `Создано смен: ${j.created}. Уже было: ${j.skipped}.`);
    } catch { notify('Ошибка', 'Нет связи'); }
    setApplying(false);
  };

  if (locations.loading || users.loading) return <Loader />;

  return (
    <View style={screen.root}>
      <StickerBackdrop seed="shablon" opacity={0.13} />
      <ScrollView contentContainerStyle={screen.content}>
        <Text style={styles.intro}>
          Опишите обычную неделю: кто в какие дни и часы работает. Потом «Заполнить график» — и смены на 2 недели вперёд создадутся сами. Раз в день график докатывается автоматически.
        </Text>

        {activeLocs.length > 1 && (
          <View style={styles.chips}>
            {activeLocs.map((l) => (
              <Pressable key={l.id} onPress={() => setLocId(l.id)} style={[styles.chip, locId === l.id && styles.chipOn]}>
                <Text style={[styles.chipText, locId === l.id && styles.chipTextOn]}>{l.name}</Text>
              </Pressable>
            ))}
          </View>
        )}

        <Pressable onPress={() => setActive((v) => !v)} style={styles.toggleRow}>
          <Text style={styles.toggleLabel}>Шаблон активен</Text>
          <Toggle value={active} onValueChange={setActive} />
        </Pressable>

        <View style={styles.week}>
          {DOW.map((d, i) => (
            <Pressable key={d} onPress={() => { setDayIdx(i); setAdding(false); }} style={[styles.day, dayIdx === i && styles.dayOn]}>
              <Text style={[styles.dayText, dayIdx === i && styles.dayTextOn]}>{d}</Text>
              {slots.some((s) => s.weekday === toWeekday(i)) ? <View style={styles.dot} /> : null}
            </Pressable>
          ))}
        </View>

        {loading ? <Loader /> : (
          <>
            <SectionLabel>{DOW[dayIdx]} — смены</SectionLabel>
            {daySlots.length === 0 && !adding && <Text style={styles.muted}>В этот день смен нет.</Text>}
            {daySlots.map(({ s, i }) => (
              <Card key={i} style={styles.slot}>
                <View style={styles.grow}>
                  <Text style={styles.slotWho}>{empName(s.employeeId)}</Text>
                  <Text style={styles.slotMeta}>{s.startLocal}–{s.endLocal} · {stName(s.shiftTypeId)}{s.position ? ` · ${s.position}` : ''}</Text>
                </View>
                <TextButton title="Убрать" kind="danger" onPress={() => removeSlot(i)} />
              </Card>
            ))}

            {adding ? (
              <Card style={styles.addForm}>
                <SectionLabel>Новая смена · {DOW[dayIdx]}</SectionLabel>
                <Text style={styles.fieldLabel}>СОТРУДНИК</Text>
                <View style={styles.chips}>
                  {usersForLoc.map((u) => (
                    <Pressable key={u.id} onPress={() => setDraft((d) => ({ ...d, employeeId: u.id }))} style={[styles.chip, draft.employeeId === u.id && styles.chipOn]}>
                      <Text style={[styles.chipText, draft.employeeId === u.id && styles.chipTextOn]}>{short(u.fullName || u.email)}</Text>
                    </Pressable>
                  ))}
                </View>
                <Text style={styles.fieldLabel}>ТИП СМЕНЫ</Text>
                <View style={styles.chips}>
                  {stForLoc.map((s) => (
                    <Pressable key={s.id} onPress={() => setDraft((d) => ({ ...d, shiftTypeId: s.id }))} style={[styles.chip, draft.shiftTypeId === s.id && styles.chipOn]}>
                      <Text style={[styles.chipText, draft.shiftTypeId === s.id && styles.chipTextOn]}>{s.name}</Text>
                    </Pressable>
                  ))}
                </View>
                {(loc?.positions || []).length > 0 && (
                  <>
                    <Text style={styles.fieldLabel}>ДОЛЖНОСТЬ (необязательно)</Text>
                    <View style={styles.chips}>
                      {loc.positions.map((p) => (
                        <Pressable key={p} onPress={() => setDraft((d) => ({ ...d, position: d.position === p ? null : p }))} style={[styles.chip, draft.position === p && styles.chipOn]}>
                          <Text style={[styles.chipText, draft.position === p && styles.chipTextOn]}>{p}</Text>
                        </Pressable>
                      ))}
                    </View>
                  </>
                )}
                <View style={styles.times}>
                  <View style={styles.grow}><Field label="Начало" value={draft.startLocal} onChangeText={(t) => setDraft((d) => ({ ...d, startLocal: t }))} placeholder="09:00" /></View>
                  <View style={styles.grow}><Field label="Конец" value={draft.endLocal} onChangeText={(t) => setDraft((d) => ({ ...d, endLocal: t }))} placeholder="21:00" /></View>
                </View>
                <View style={styles.formBtns}>
                  <View style={styles.grow}><PrimaryButton title="Добавить" onPress={commitAdd} /></View>
                  <TextButton title="Отмена" kind="muted" onPress={() => { setAdding(false); setDraft(null); }} />
                </View>
              </Card>
            ) : (
              <TextButton title="＋ Добавить смену" onPress={startAdd} />
            )}

            <View style={styles.actions}>
              <PrimaryButton title="Сохранить шаблон" onPress={save} loading={saving} />
              <PrimaryButton title="Заполнить график на 2 недели" kind="primary" onPress={apply} loading={applying} />
            </View>
          </>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  intro: { ...typography.caption, color: colors.textSecondary },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginVertical: spacing.xs },
  chip: { backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.full, paddingHorizontal: spacing.md, paddingVertical: 7 },
  chipOn: { borderColor: colors.primary, backgroundColor: 'rgba(237,42,35,0.08)' },
  chipText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  chipTextOn: { color: colors.primary },
  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.md, marginTop: spacing.sm },
  toggleLabel: { ...typography.body, color: colors.textPrimary },
  week: { flexDirection: 'row', gap: 4, marginTop: spacing.md },
  day: { flex: 1, alignItems: 'center', paddingVertical: 9, borderRadius: radius.sm, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border },
  dayOn: { borderColor: colors.primary, backgroundColor: 'rgba(237,42,35,0.08)' },
  dayText: { ...typography.caption, color: colors.textSecondary, fontWeight: '800' },
  dayTextOn: { color: colors.primary },
  dot: { width: 5, height: 5, borderRadius: 3, backgroundColor: colors.primary, marginTop: 3 },
  muted: { ...typography.body, color: colors.textMuted },
  slot: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  grow: { flex: 1 },
  slotWho: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 14 },
  slotMeta: { ...typography.caption, color: colors.textMuted, marginTop: 1 },
  addForm: { gap: spacing.xs },
  fieldLabel: { ...typography.caption, color: colors.textMuted, fontWeight: '800', letterSpacing: 1, marginTop: spacing.sm },
  times: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.xs },
  formBtns: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginTop: spacing.sm },
  actions: { gap: spacing.sm, marginTop: spacing.lg },
});
